/**
 * Feature Access Hooks
 * 
 * React hooks for checking and managing tenant feature access.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getTenantFeatures,
  checkFeatureAccess,
  isFeatureEnabled as checkFeatureEnabled,
} from '../services/featureService';
import { TenantFeature } from '../types/admin';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to get all enabled features for the current tenant
 */
export function useCurrentTenantFeatures() {
  const { currentUser } = useAuth();
  const [features, setFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFeatures = useCallback(async () => {
    // If no tenantId, return empty features (e.g., Platform Admin without tenant)
    if (!currentUser?.tenantId) {
      setFeatures([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getTenantFeatures(currentUser.tenantId);
      setFeatures(response.enabledFeatures);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch tenant features:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.tenantId]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return {
    features,
    loading,
    error,
    refetch: fetchFeatures,
  };
}

/**
 * Hook to check if a specific feature is enabled
 */
export function useFeatureAccess(feature: TenantFeature) {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const checkAccess = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await checkFeatureAccess(feature);
      setHasAccess(response.hasAccess);
    } catch (err) {
      setError(err as Error);
      setHasAccess(false);
      console.error(`Failed to check access for feature ${feature}:`, err);
    } finally {
      setLoading(false);
    }
  }, [feature]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  return {
    hasAccess,
    loading,
    error,
    refetch: checkAccess,
  };
}

/**
 * Hook to check if a feature is enabled (using cached features list)
 * More efficient than useFeatureAccess when checking multiple features
 */
export function useIsFeatureEnabled(feature: TenantFeature) {
  const { features, loading, error } = useCurrentTenantFeatures();
  const isEnabled = checkFeatureEnabled(features, feature);

  return {
    isEnabled,
    loading,
    error,
  };
}

/**
 * Hook to get multiple feature access states at once
 */
export function useMultipleFeatureAccess(features: TenantFeature[]) {
  const { features: enabledFeatures, loading, error } = useCurrentTenantFeatures();

  const featureAccess = features.reduce((acc, feature) => {
    acc[feature] = checkFeatureEnabled(enabledFeatures, feature);
    return acc;
  }, {} as Record<TenantFeature, boolean>);

  return {
    featureAccess,
    loading,
    error,
  };
}
